import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import '../pdf-worker';
import Sidebar from './Sidebar';
import LineItems from './LineItems';
import PDFHighlight from './PDFHighlight';
import PDFTextSelector from './PDFTextSelector';
import './InvoiceViewer.css';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

function InvoiceViewer() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [file, setFile] = useState(null);
  const [extractedDataPerPage, setExtractedDataPerPage] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [showLineItems, setShowLineItems] = useState(true);
  const [pdfTextItems, setPdfTextItems] = useState([]);
  const [highlightedFields, setHighlightedFields] = useState([]);
  const pdfContainerRef = useRef(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
  const [pdfViewport, setPdfViewport] = useState(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });
  const [activeField, setActiveField] = useState(null);
  const [activeFieldRect, setActiveFieldRect] = useState(null);
  const [activeLineItemIndex, setActiveLineItemIndex] = useState(null);
  const sidebarRef = useRef(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [category, setCategory] = useState('Uncategorized');
  const [loadingQuote, setLoadingQuote] = useState("");
  const categories = ['Delivery Notes', 'Invoices', 'Purchase Orders', 'Credit Notes', 'Debit Notes'];
  
  const [invoicePages, setInvoicePages] = useState([]); // To store page numbers that contain "Invoice"

  useEffect(() => {
    // Set a cool quote for the loading state
    const quotes = [
      "DocExtract is simplifying document extraction for you!",
      "Processing your document with DocExtract accuracy and speed.",
      "Making document processing hassle-free, one file at a time with DocExtract.",
      "DocExtract: your trusted partner in document extraction.",
      "Working behind the scenes to extract valuable information with DocExtract."
    ];
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setLoadingQuote(randomQuote);
  }, []);

  const mergeExtractedData = useCallback((existingData, newData) => {
  // Deep clone the existing data to avoid mutating state directly
    const mergedData = JSON.parse(JSON.stringify(existingData));
  
    // Merge unique_fields
    mergedData.unique_fields = { ...newData.unique_fields, ...existingData.unique_fields };
  
    // Merge total_bill
    mergedData.total_bill = { ...newData.total_bill, ...existingData.total_bill };
  
    // Merge line_items
    if (Array.isArray(newData.line_items) && Array.isArray(existingData.line_items)) {
      mergedData.line_items = newData.line_items.map((item, index) => {
        if (existingData.line_items[index]) {
          return { ...item, ...existingData.line_items[index] };
        }
        return item;
      });
    } else {
      mergedData.line_items = newData.line_items || existingData.line_items;
    }
  
    return mergedData;
  }, []);

  const extractTextFromPDF = useCallback(async (fileContent) => {
    try {
      console.log("Starting PDF text extraction to find invoice pages");
      const pdf = await pdfjs.getDocument({ data: atob(fileContent.split(',')[1]) }).promise;
      const numPages = pdf.numPages;
      const invoicePagesArray = [];
  
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
  
        // Check if this page contains "GST invoice" or "tax invoice"
        const pageText = textContent.items.map(item => item.str.toLowerCase()).join(" ");
        if (pageText.includes("gst invoice") || pageText.includes("tax invoice")) {
          invoicePagesArray.push(i);
        }
      }
  
      if (invoicePagesArray.length === 0) {
        for (let i = 1; i <= numPages; i++) {
          invoicePagesArray.push(i);
        }
      }
  
      setInvoicePages(invoicePagesArray);
      setPageNumber(invoicePagesArray[0]);
  
    } catch (e) {
      console.error('Failed to extract text from PDF:', e);
      setError('Failed to extract text from PDF: ' + e.message);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
  
      try {
        setIsLoading(true);
        const token = await currentUser.getIdToken();
  
        // Fetch document content
        const response = await fetch(`${API_BASE_URL}/document/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        if (!response.ok) {
          throw new Error(`Error fetching document: ${response.statusText}`);
        }
  
        const data = await response.json();
        setFile(data.content);
        setCategory(data.category || 'Uncategorized');
        // Fetch extracted data
        const dataResponse = await fetch(`${API_BASE_URL}/get_data/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
  
        if (dataResponse.ok) {
          const extractedData = await dataResponse.json();
          setExtractedDataPerPage(extractedData);
        } else {
          console.log('No extracted data found');
        }
  
        // Extract invoice pages (falls back to all pages if invoice keyword is absent)
        await extractTextFromPDF(data.content);
      } catch (error) {
        console.error('Error fetching document:', error);
        setError('Error fetching document: ' + error.message);
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchData();
  }, [currentUser, extractTextFromPDF, id]);

  const fetchInvoiceData = useCallback(async (currentPageNumber) => {
    if (!currentUser) {
      setError('User not authenticated');
      return;
    }
    try {
      setIsLoading(true);
      const token = await currentUser.getIdToken();
  
      const response = await fetch(`${API_BASE_URL}/process_document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          document_id: id,
          page_number: currentPageNumber,
        }),
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log("Received data from API:", data);
  
      setExtractedDataPerPage((prevData) => {
        const existingPageData = prevData[currentPageNumber] || {};
        const mergedData = mergeExtractedData(existingPageData, data);
        return {
          ...prevData,
          [currentPageNumber]: mergedData,
        };
      });
    } catch (e) {
      console.error('Failed to fetch invoice data:', e);
      setError('Failed to fetch invoice data: ' + e.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, id, mergeExtractedData]);

  useEffect(() => {
    if (pageNumber) {
      fetchInvoiceData(pageNumber);
    }
  }, [fetchInvoiceData, pageNumber]);
  
  const groupTextItemsIntoLines = useCallback((textItems) => {
    const lines = [];
    const lineThreshold = 5; // Adjust this threshold based on your PDF's text line spacing
  
    // Sort text items by their Y position (from top to bottom)
    const sortedItems = textItems.slice().sort((a, b) => b.transform[5] - a.transform[5]);
  
    let currentLine = [];
    let lastY = null;
  
    sortedItems.forEach((item) => {
      const y = item.transform[5];
  
      if (lastY === null || Math.abs(y - lastY) < lineThreshold) {
        currentLine.push(item);
      } else {
        if (currentLine.length > 0) {
          lines.push(currentLine);
        }
        currentLine = [item];
      }
      lastY = y;
    });
  
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }
  
    return lines;
  }, []);

  const findHighlightedFields = useCallback((textItems, extractedData) => {
    console.log("Finding highlighted fields");
    console.log("Extracted Data:", JSON.stringify(extractedData, null, 2));
    const highlights = [];
    const lines = groupTextItemsIntoLines(textItems);
    const processField = (field, value, isExact = false, lineItemIndex = null) => {
      // Skip processing for serial numbers and Invoice Line Item Numbers
      if (field.includes('S.No') || field.includes('Invoice Line Item Number')) {
        console.log(`Skipping serial number field: ${field}`);
        return;
      }
  
      if (value && value.toString().trim() !== '') {
        console.log(`Processing field: ${field} with value: ${value}`);
        const valueStr = value.toString().trim();
        const valueNumeric = parseFloat(valueStr.replace(/,/g, ''));
        const isNumeric = !isNaN(valueNumeric);
  
        if ((field === 'CGST' || field === 'SGST') && isNumeric) {
          const normalizedField = normalizeText(field);
  
          // Iterate over lines to find labels and values
          for (const line of lines) {
            // Check if line contains the label
            const hasLabel = line.some(item => {
              const normalizedItemStr = normalizeText(item.str);
              return normalizedItemStr.includes(normalizedField);
            });
  
            if (hasLabel) {
              // Find numeric values in the same line
              const valueItems = line.filter(item => {
                const itemStr = item.str.trim();
                const itemNumeric = parseFloat(itemStr.replace(/,/g, ''));
                return !isNaN(itemNumeric) && Math.abs(itemNumeric - valueNumeric) < 0.01;
              });
  
              if (valueItems.length > 0) {
                // Use the first matching value item
                const valueItem = valueItems[0];
                addHighlight(valueItem, field, valueStr, lineItemIndex);
                break; // Stop searching after finding the first match
              }
            }
          }
        } else {
          // Existing matching logic
          const matchingItems = textItems.filter(item => {
            const itemStr = item.str.trim();
            if (field === 'Invoice Number') {
              return itemStr.replace(/^0+/, '').includes(valueStr.replace(/^0+/, ''));
            } else if (field.includes('GST Number')) {
              return itemStr.replace(/\s/g, '').includes(valueStr.replace(/\s/g, ''));
            } else if (field.includes('Date')) {
              const dateRegex = new RegExp(valueStr.split('-').join('[.-]'));
              return dateRegex.test(itemStr);
            } else if (isNumeric) {
              const itemNumeric = parseFloat(itemStr.replace(/,/g, ''));
              return Math.abs(itemNumeric - valueNumeric) < 0.01;
            } else {
              return itemStr.toLowerCase().includes(valueStr.toLowerCase());
            }
          });
  
          if (matchingItems.length > 0) {
            addHighlight(matchingItems[0], field, valueStr, lineItemIndex);
          } else {
            console.log(`No match found for ${field}: ${valueStr}`);
          }
        }
      }
    };

    const normalizeText = (text) => {
      return text.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    };


    const addHighlight = (item, field, valueStr, lineItemIndex = null) => {
      const itemStr = item.str;
      let startIndex = itemStr.toLowerCase().indexOf(valueStr.toLowerCase());

      if (startIndex === -1) startIndex = 0;
      if (startIndex > 0) startIndex--;

      const charWidth = item.width / item.str.length;
      const x = item.transform[4] + (startIndex * charWidth);
      const width = (Math.min(valueStr.length, itemStr.length - startIndex) + 1) * charWidth;
      const y = item.transform[5];
      const height = item.height;

      highlights.push({
        field,
        value: valueStr,
        coordinates: [x, y, width, height],
        lineItemIndex,
      });
      console.log(`Found match for ${field}: ${valueStr} at`, [x, y, width, height]);
    };

    // Process unique fields
    if (extractedData.unique_fields) {
      Object.entries(extractedData.unique_fields).forEach(([field, value]) => {
        processField(field, value, field === 'Vendor GST Number');
      });
    }

    // Process total bill items
    if (extractedData.total_bill) {
      Object.entries(extractedData.total_bill).forEach(([field, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          processField(field, value.toString(), false, null);
        }
      });
    }

    // Process line items
    if (extractedData.line_items) {
      extractedData.line_items.forEach((item, index) => {
        Object.entries(item).forEach(([field, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            processField(field, value.toString(), false, index);
          }
        });
      });
    }

    console.log('Highlights:', highlights);
    setHighlightedFields(highlights);
  }, [groupTextItemsIntoLines]);

  const handleLineItemsToggle = (isExpanded) => {
    setShowLineItems(isExpanded);
  };

  const handleActivateField = (field, lineItemIndex) => {
    console.log("Activating field:", field);

    if (!field) {
      // Clear active field and line
      setActiveField(null);
      setActiveFieldRect(null);
      setActiveLineItemIndex(null);
      return;
    }
  
    if (pdfContainerRef.current) {
      const lineItemIndexAttr = lineItemIndex !== null && lineItemIndex !== undefined ? lineItemIndex : '';
      const pdfHighlightElement = pdfContainerRef.current.querySelector(
        `[data-field="${field}"][data-lineitemindex="${lineItemIndexAttr}"]`
      );
  
      if (pdfHighlightElement) {
        const rect = pdfHighlightElement.getBoundingClientRect();
        setActiveField(field);
        setActiveFieldRect({
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        });
        setActiveLineItemIndex(lineItemIndex);
      } else {
        console.log("No matching highlight element found in PDF for field:", field);
        // Clear active field and line
        setActiveField(null);
        setActiveFieldRect(null);
        setActiveLineItemIndex(null);
        //alert(`No matching value found in the PDF for field "${field}".`);
      }
    }
  };

  const handleHighlightUpdate = (field, newValue, lineItemIndex) => {
    setExtractedDataPerPage(prevData => {
      const pageData = prevData[pageNumber] || {};
      const newData = { ...pageData };
  
      if (lineItemIndex !== null && lineItemIndex !== undefined && newData.line_items) {
        // Update specific line item
        newData.line_items = newData.line_items.map((item, index) => {
          if (index === lineItemIndex) {
            return { ...item, [field]: newValue };
          }
          return item;
        });
      } else if (newData.unique_fields && field in newData.unique_fields) {
        newData.unique_fields[field] = newValue;
      } else if (newData.total_bill && field in newData.total_bill) {
        newData.total_bill[field] = newValue;
      }
  
      return {
        ...prevData,
        [pageNumber]: newData,
      };
    });
  
    setHighlightedFields(prevFields =>
      prevFields.map(highlightField =>
        highlightField.field === field && highlightField.lineItemIndex === lineItemIndex
          ? { ...highlightField, value: newValue }
          : highlightField
      )
    );
  };
  
  const handleTextSelected = (selectedText, coordinates) => {
    const fieldName = prompt("Assign this text to which field?");
    if (fieldName) {
      handleHighlightUpdate(fieldName, selectedText);
      setHighlightedFields(prevFields => [
        ...prevFields,
        { field: fieldName, value: selectedText, coordinates }
      ]);
    }
  };

  // InvoiceViewer.js
  const renderConnectingLine = () => {
    if (!activeField || !sidebarRef.current || !activeFieldRect) {
      return null;
    }
  
    const sidebarField = sidebarRef.current.querySelector(`[data-field="${activeField}"]`);
    if (!sidebarField) {
      return null;
    }
  
    const sidebarRect = sidebarField.getBoundingClientRect();
  
    // Starting point (Sidebar field)
    const startX = sidebarRect.right;
    const startY = sidebarRect.top + sidebarRect.height / 2;
  
    // Ending point (PDF highlight)
    const endX = activeFieldRect.left + activeFieldRect.width / 2;
    const endY = activeFieldRect.top + activeFieldRect.height / 2;
  
    const midX = (startX + endX) / 2;
  
    return (
      <svg
        className="connecting-line-svg"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 1002,
        }}
      >
        <path
          d={`M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`}
          fill="none"
          stroke="#007bff"
          strokeWidth="2"
        />
        <circle cx={endX} cy={endY} r="4" fill="#007bff" />
      </svg>
    );
  };


  const onPageLoadSuccess = async (page) => {
    const viewport = page.getViewport({ scale: 1 });
    const container = pdfContainerRef.current;
  
    if (container) {
      const { width: containerWidth, height: containerHeight } = container.getBoundingClientRect();
      setContainerDimensions({ width: containerWidth, height: containerHeight });
      const scale = containerWidth / viewport.width;
      setPdfViewport(viewport);
  
      console.log('PDF original dimensions:', viewport.width, viewport.height);
      console.log('Container dimensions:', containerWidth, containerHeight);
      console.log('PDF scale factor:', scale);
  
      // Set pdfDimensions here
      setPdfDimensions({ width: viewport.width, height: viewport.height });
  
      // Extract text content from the page
      const textContent = await page.getTextContent();
      const textItems = textContent.items;
  
      // Update text items for highlighting
      setPdfTextItems(textItems);
    }
  };

  useEffect(() => {
    const extractedData = extractedDataPerPage[pageNumber] || null;
    if (
      pdfTextItems.length > 0 &&
      extractedData &&
      pdfViewport &&
      containerDimensions.width > 0 &&
      containerDimensions.height > 0 &&
      pdfDimensions.width > 0 &&
      pdfDimensions.height > 0
    ) {
      console.log("All dependencies are ready, finding highlighted fields");
      findHighlightedFields(
        pdfTextItems,
        extractedData
      );
    } else {
      console.log("Waiting for dependencies to be ready");
    }
  }, [containerDimensions, extractedDataPerPage, findHighlightedFields, pageNumber, pdfDimensions, pdfTextItems, pdfViewport]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (pdfContainerRef.current) {
      const { width, height } = pdfContainerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }
  }, [windowSize]);

  useEffect(() => {
    const handleScroll = () => {
      if (activeField) {
        // Clear line while scrolling to avoid stale pointer geometry.
        setActiveFieldRect(null);
      }
    };

    const pdfContainer = pdfContainerRef.current;
    if (pdfContainer) {
      pdfContainer.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (pdfContainer) {
        pdfContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [activeField]);
  const handleCategoryChange = (event) => {
    setCategory(event.target.value);
  };



  const saveEditedData = async () => {
    if (!currentUser) {
      setError('User not authenticated');
      return;
    }
    try {
      const token = await currentUser.getIdToken();
      // Save the edited data
      const saveResponse = await fetch(`${API_BASE_URL}/save_edits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          document_id: id,
          edited_data: extractedDataPerPage,
        }),
      });
      if (!saveResponse.ok) {
        throw new Error(`HTTP error! status: ${saveResponse.status}`);
      }
  
      // Update the document's status and category
      const updateResponse = await fetch(`${API_BASE_URL}/update_document_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          document_id: id,
          status: 'Confirmed',
          category: category,
        }),
      });
      if (!updateResponse.ok) {
        throw new Error(`HTTP error! status: ${updateResponse.status}`);
      }
  
      alert('Data saved and document confirmed successfully!');
      // Optionally, navigate back to the Document List page
    } catch (e) {
      console.error('Failed to save data:', e);
      setError('Failed to save data: ' + e.message);
    }
  };




  const rejectDocument = async () => {
    if (!currentUser) {
      setError('User not authenticated');
      return;
    }
    try {
      const token = await currentUser.getIdToken();
  
      // Update the document's status to "Rejected"
      const updateResponse = await fetch(`${API_BASE_URL}/update_document_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          document_id: id,
          status: 'Rejected',
        }),
      });
      if (!updateResponse.ok) {
        throw new Error(`HTTP error! status: ${updateResponse.status}`);
      }
  
      alert('Document has been rejected successfully!');
      // Optionally, navigate back to the Document List page
    } catch (e) {
      console.error('Failed to reject document:', e);
      setError('Failed to reject document: ' + e.message);
    }
  };

  const handleNextPage = () => {
    const currentIndex = invoicePages.indexOf(pageNumber);
    if (currentIndex < invoicePages.length - 1) {
      setPageNumber(invoicePages[currentIndex + 1]);
    }
  };
  
  const handlePreviousPage = () => {
    const currentIndex = invoicePages.indexOf(pageNumber);
    if (currentIndex > 0) {
      setPageNumber(invoicePages[currentIndex - 1]);
    }
  };
  
  


  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-quote">{loadingQuote}</p>
      </div>
    );
  }
  if (error) return <div className="error">{error}</div>;

  const extractedData = extractedDataPerPage[pageNumber] || null;

  return (
    <div className="invoice-viewer">
      {extractedData ? (
        <>
          <Sidebar
            ref={sidebarRef}
            extractedData={extractedData}
            onLineItemsToggle={handleLineItemsToggle}
            onFieldUpdate={handleHighlightUpdate}
            onFieldActivate={handleActivateField}
          />
          <div className="main-content">
            <div className="pagination-controls">
              <button onClick={handlePreviousPage} disabled={invoicePages.indexOf(pageNumber) <= 0}>
                Previous
              </button>
              <span>Page {invoicePages.indexOf(pageNumber) + 1} of {invoicePages.length}</span>
              <button onClick={handleNextPage} disabled={invoicePages.indexOf(pageNumber) >= invoicePages.length - 1}>
                Next
              </button>
            </div>  
            <div className="pdf-viewer" ref={pdfContainerRef}>
              {file && (
                <Document
                  file={file}
                  onLoadError={(error) => {
                    console.error('Failed to load PDF:', error);
                    setError('Failed to load PDF: ' + error.message);
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <Page
                      pageNumber={pageNumber}
                      width={containerDimensions.width}
                      onLoadSuccess={onPageLoadSuccess}
                    >
                      {highlightedFields.map((field, index) => (
                        <PDFHighlight
                          key={`${field.field}-${index}-${field.lineItemIndex ?? 'non-line-item'}`}
                          field={field.field}
                          coordinates={field.coordinates}
                          pdfDimensions={pdfDimensions}
                          containerDimensions={containerDimensions}
                          value={field.value}
                          onUpdate={handleHighlightUpdate}
                          onActivate={handleActivateField}
                          lineItemIndex={field.lineItemIndex ?? null}
                          activeField={activeField}
                          activeLineItemIndex={activeLineItemIndex}
                        />
                      ))}
                      <PDFTextSelector onTextSelected={handleTextSelected} />
                    </Page>
                  </div>
                </Document>
              )}
              {renderConnectingLine()}
            </div>
            <div className="category-action-container">
              <div className="category-selection">
                <label>Category:</label>
                <select value={category} onChange={handleCategoryChange}>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="action-buttons">
                <button className="reject-button" onClick={rejectDocument}>Reject</button>
                <button className="save-button" onClick={saveEditedData}>Save</button>
              </div>
            </div>
            <div className={`line-items-container ${showLineItems ? 'show' : 'hide'}`}>
              {console.log('Line items:', extractedData?.line_items)}
              <LineItems items={extractedData?.line_items || []} onUpdate={handleHighlightUpdate} />
            </div>
            
          </div>
        </>
      ) : (
        <div>Loading extracted data...</div>
      )}
    </div>
  );
}

export default InvoiceViewer;
